import { useState, useCallback } from "react";
import { GarmentMeasurementResult, GarmentMeasurement, ImageUpload } from "../types/unified-services";
import { compressBase64Image, getBase64Size } from "../helpers/imageCompression";

export interface GarmentMeasurementState {
  images: ImageUpload[];
  currentImageIndex: number;
  maxImagesCount: number;
  result: GarmentMeasurementResult | null;
  allMeasurements: (GarmentMeasurementResult | null)[];
  selectedMeasurement: string | null;
  loadingResult: boolean;
  measurementUnit: "cm" | "inch";
  imageErrors: string[];
  processingStatus: { [key: number]: "pending" | "processing" | "success" | "error" };

  image: ImageUpload;
}

export interface GarmentMeasurementActions {
  setImages: (images: ImageUpload[]) => void;
  setImage: (image: ImageUpload, index: number) => void;
  addNewImageSlot: () => void;
  removeImage: (index: number) => void;
  setCurrentImageIndex: (index: number) => void;
  setResult: (result: GarmentMeasurementResult | null) => void;
  setAllMeasurements: (measurements: (GarmentMeasurementResult | null)[]) => void;
  setSelectedMeasurement: (measurement: string | null) => void;
  setLoadingResult: (loading: boolean) => void;
  setMeasurementUnit: (unit: "cm" | "inch") => void;
  setImageError: (index: number, error: string) => void;
  setProcessingStatus: (index: number, status: "pending" | "processing" | "success" | "error") => void;
  resetGarmentMeasurement: () => void;
  getMeasurement: (key: string) => GarmentMeasurement | null;
  convertMeasurement: (value: number, fromUnit: "cm" | "inch", toUnit: "cm" | "inch") => number;
  handleImageUpload: (file: File, index: number) => Promise<void>;
  handleMultipleImagesUpload: (files: FileList) => Promise<void>;
}

export const useGarmentMeasurementState = (): GarmentMeasurementState & GarmentMeasurementActions => {
  const MAX_IMAGES_COUNT = 50;

  const createInitialImageUpload = (): ImageUpload => ({
    file: null,
    base64: null,
    preview: null,
    isValid: false,
    error: null,
  });

  const [images, setImages] = useState<ImageUpload[]>([createInitialImageUpload()]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [result, setResult] = useState<GarmentMeasurementResult | null>(null);
  const [allMeasurements, setAllMeasurements] = useState<(GarmentMeasurementResult | null)[]>([]);
  const [selectedMeasurement, setSelectedMeasurement] = useState<string | null>(null);
  const [loadingResult, setLoadingResult] = useState(false);
  const [measurementUnit, setMeasurementUnit] = useState<"cm" | "inch">("cm");
  const [imageErrors, setImageErrors] = useState<string[]>([""]);
  const [processingStatus, setProcessingStatus] = useState<{ [key: number]: "pending" | "processing" | "success" | "error" }>({});

  const setImage = useCallback(async (imageUpload: ImageUpload, index: number) => {
    setImages((prev) => {
      const newImages = [...prev];
      newImages[index] = imageUpload;
      return newImages;
    });
  }, []);

  const addNewImageSlot = useCallback(() => {
    if (images.length < MAX_IMAGES_COUNT) {
      setImages((prev) => [...prev, createInitialImageUpload()]);
      setImageErrors((prev) => [...prev, ""]);
    }
  }, [images.length, MAX_IMAGES_COUNT]);

  const removeImage = useCallback(
    (index: number) => {
      setImages((prev) => {
        const updated = [...prev];
        // If there's only one image slot, reset it to initial state instead of removing
        if (updated.length === 1) {
          updated[0] = createInitialImageUpload();
          return updated;
        }
        // Otherwise filter out the image at the given index
        return updated.filter((_, i) => i !== index);
      });
      setImageErrors((prev) => {
        if (prev.length === 1) {
          return [""];
        }
        return prev.filter((_, i) => i !== index);
      });
      if (currentImageIndex >= images.length - 1) {
        setCurrentImageIndex(Math.max(0, images.length - 2));
      }
    },
    [images.length, currentImageIndex]
  );

  const setImageError = useCallback((index: number, error: string) => {
    setImageErrors((prev) => {
      const newErrors = [...prev];
      newErrors[index] = error;
      return newErrors;
    });
  }, []);

  const handleImageUpload = useCallback(
    async (file: File, index: number) => {
      const validation = { isValid: true, error: undefined };

      const newImageState: ImageUpload = {
        file,
        base64: null,
        preview: URL.createObjectURL(file),
        isValid: validation.isValid,
        error: validation.error,
      };

      if (validation.isValid) {
        try {
          const reader = new FileReader();
          reader.onload = async () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(",")[1];

            // Log original size
            const originalSize = getBase64Size(base64Data);
            console.log(`Original image size: ${originalSize.toFixed(2)} MB`);

            // Compress image to prevent iOS Safari crashes
            try {
              const compressedBase64 = await compressBase64Image(base64Data, {
                maxWidth: 1200,
                maxHeight: 1200,
                quality: 0.85,
              });

              const compressedSize = getBase64Size(compressedBase64);
              console.log(`Compressed image size: ${compressedSize.toFixed(2)} MB (${((1 - compressedSize / originalSize) * 100).toFixed(1)}% reduction)`);

              newImageState.base64 = compressedBase64;
            } catch (compressionError) {
              console.warn("Image compression failed, using original:", compressionError);
              newImageState.base64 = base64Data;
            }

            setImage(newImageState, index);
          };
          reader.readAsDataURL(file);
        } catch (error) {
          newImageState.isValid = false;
          newImageState.error = "Failed to process image";
          setImage(newImageState, index);
        }
      } else {
        setImage(newImageState, index);
      }
    },
    [setImage]
  );

  const handleMultipleImagesUpload = useCallback(
    async (files: FileList) => {
      const filesArray = Array.from(files);
      for (let i = 0; i < filesArray.length && i < MAX_IMAGES_COUNT; i++) {
        if (i >= images.length) {
          addNewImageSlot();
        }
        await handleImageUpload(filesArray[i], i);
      }
    },
    [images.length, addNewImageSlot, MAX_IMAGES_COUNT, handleImageUpload]
  );

  const getMeasurement = useCallback(
    (key: string): GarmentMeasurement | null => {
      return result?.Measurements[key] || null;
    },
    [result]
  );

  const convertMeasurement = useCallback((value: number, fromUnit: "cm" | "inch", toUnit: "cm" | "inch"): number => {
    if (fromUnit === toUnit) return value;

    if (fromUnit === "cm" && toUnit === "inch") {
      return value / 2.54;
    } else if (fromUnit === "inch" && toUnit === "cm") {
      return value * 2.54;
    }

    return value;
  }, []);

  const setProcessingStatusCallback = useCallback((index: number, status: "pending" | "processing" | "success" | "error") => {
    setProcessingStatus((prev) => ({
      ...prev,
      [index]: status,
    }));
  }, []);

  const resetGarmentMeasurement = useCallback(() => {
    setImages([createInitialImageUpload()]);
    setCurrentImageIndex(0);
    setResult(null);
    setAllMeasurements([]);
    setSelectedMeasurement(null);
    setLoadingResult(false);
    setMeasurementUnit("cm");
    setImageErrors([""]);
    setProcessingStatus({});
  }, []);

  return {
    images,
    currentImageIndex,
    maxImagesCount: MAX_IMAGES_COUNT,
    result,
    allMeasurements,
    selectedMeasurement,
    loadingResult,
    measurementUnit,
    imageErrors,
    processingStatus,

    image: images[currentImageIndex] || createInitialImageUpload(),

    setImages,
    setImage,
    addNewImageSlot,
    removeImage,
    setCurrentImageIndex,
    setResult,
    setAllMeasurements,
    setSelectedMeasurement,
    setLoadingResult,
    setMeasurementUnit,
    setImageError,
    setProcessingStatus: setProcessingStatusCallback,
    resetGarmentMeasurement,
    getMeasurement,
    convertMeasurement,
    handleImageUpload,
    handleMultipleImagesUpload,
  };
};
